import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { convertMarkdownToDocx, convertMarkdownToPdf } from '@/lib/docx-utils'
import { Copy, Download, ExternalLink, FileText, Maximize2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ContextMenuWrapper } from './ContextMenuWrapper'
import {
  csvToMarkdownTable,
  resolveListTemplateVariables,
  resolveTemplateVariables,
} from './renderingUtils'

/**
 * Downloads content as DOCX file using pandoc conversion
 */
async function downloadAsDocx(content: string, filename: string = 'document.docx') {
  try {
    const blob = await convertMarkdownToDocx(content)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
    toast.success('DOCX file downloaded successfully')
  } catch (error) {
    console.error('Error creating DOCX:', error)
    toast.error('Failed to download DOCX file')
  }
}

/**
 * Downloads content as PDF file using pandoc conversion
 */
async function downloadAsPdf(content: string, filename: string = 'document.pdf') {
  try {
    const blob = await convertMarkdownToPdf(content)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
    toast.success('PDF file downloaded successfully')
  } catch (error) {
    console.error('Error creating PDF:', error)
    toast.error('Failed to download PDF file')
  }
}

/**
 * Detects file type from URL
 */
function getFileTypeFromUrl(url: string): { type: string; icon: string } {
  const lowercaseUrl = url.toLowerCase()

  if (lowercaseUrl.includes('.pdf')) return { type: 'PDF Document', icon: '📄' }
  if (lowercaseUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/))
    return { type: 'Image', icon: '🖼️' }
  if (lowercaseUrl.match(/\.(mp4|mov|avi|webm)(\?|$)/)) return { type: 'Video', icon: '🎥' }
  if (lowercaseUrl.match(/\.(mp3|wav|ogg|m4a)(\?|$)/)) return { type: 'Audio', icon: '🎵' }
  if (lowercaseUrl.match(/\.(doc|docx)(\?|$)/)) return { type: 'Word Document', icon: '📝' }
  if (lowercaseUrl.match(/\.(xls|xlsx)(\?|$)/)) return { type: 'Excel Spreadsheet', icon: '📊' }
  if (lowercaseUrl.match(/\.(ppt|pptx)(\?|$)/)) return { type: 'PowerPoint', icon: '📈' }
  if (lowercaseUrl.includes('.html') || lowercaseUrl.includes('.htm'))
    return { type: 'HTML Page', icon: '🌐' }

  return { type: 'File', icon: '📎' }
}

/**
 * HTML Viewer component
 */
export function HtmlContentViewer({ content }: { content: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [blobUrl, setBlobUrl] = useState<string>('')

  useEffect(() => {
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' })
    const newBlobUrl = URL.createObjectURL(blob)
    setBlobUrl(newBlobUrl)

    return () => {
      URL.revokeObjectURL(newBlobUrl)
    }
  }, [content])

  const handleFullscreen = useCallback(() => {
    if (blobUrl) {
      window.open(blobUrl, '_blank')
    }
  }, [blobUrl])

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3]">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white p-3">
        <div className="text-sm font-medium text-gray-700">HTML Content</div>
        <button
          onClick={handleFullscreen}
          className="rounded p-1 text-gray-600 hover:bg-gray-100"
          title="Open in new tab"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="h-80">
        {blobUrl && (
          <iframe
            ref={iframeRef}
            src={blobUrl}
            title="HTML Content"
            className="h-full w-full"
            sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            style={{ backgroundColor: 'white', border: 'none' }}
          />
        )}
      </div>
    </div>
  )
}

interface DynamicContentRendererProps {
  type: string
  content: string
  outputs: unknown
  nodeId: string
  onOpenPanel?: (content: any, breadcrumbs: any[]) => void
  enableDownloads?: boolean
}

/**
 * Unified component for dynamically rendering various content types with template variable resolution.
 * Resolves [[variable]] patterns and renders as markdown, HTML, CSV, URL, etc.
 * Used by both NativeLlmResponseText and NativeCodeRunner displays.
 */
export function DynamicContentRenderer({
  type,
  content,
  outputs,
  nodeId,
  onOpenPanel,
  enableDownloads = false,
}: DynamicContentRendererProps) {
  const resolvedContent = useMemo(() => {
    return type === 'list'
      ? resolveListTemplateVariables(content, outputs, nodeId)
      : resolveTemplateVariables(content, outputs, nodeId)
  }, [type, content, outputs, nodeId])

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(resolvedContent)
      .then(() => toast.success('Content copied to clipboard'))
      .catch(() => toast.error('Failed to copy content'))
  }, [resolvedContent])

  const handleOpenFile = useCallback(() => {
    window.open(resolvedContent, '_blank', 'noopener,noreferrer')
  }, [resolvedContent])

  const handleDownloadDocx = useCallback(async () => {
    const filename = `${type}-${Date.now()}.docx`
    await downloadAsDocx(resolvedContent, filename)
  }, [resolvedContent, type])

  const handleDownloadPdf = useCallback(async () => {
    const filename = `${type}-${Date.now()}.pdf`
    await downloadAsPdf(resolvedContent, filename)
  }, [resolvedContent, type])

  const handleOpenInPanel = useCallback(() => {
    if (onOpenPanel) {
      onOpenPanel(resolvedContent, [
        { label: 'Results', path: [] },
        { label: 'Content', path: ['content'] },
      ])
    }
  }, [resolvedContent, onOpenPanel])

  const contextActions = [
    {
      id: 'copy',
      label: 'Copy content',
      icon: <Copy className="h-4 w-4" />,
      onClick: handleCopy,
    },
    ...(enableDownloads && (type === 'markdown' || type === 'list')
      ? [
          {
            id: 'download-docx',
            label: 'Download as DOCX',
            icon: <Download className="h-4 w-4" />,
            onClick: handleDownloadDocx,
          },
          {
            id: 'download-pdf',
            label: 'Download as PDF',
            icon: <FileText className="h-4 w-4" />,
            onClick: handleDownloadPdf,
          },
        ]
      : []),
    ...(type === 'url'
      ? [
          {
            id: 'open-file',
            label: 'Open in new tab',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: handleOpenFile,
          },
        ]
      : []),
    ...(onOpenPanel && (type === 'markdown' || type === 'list' || type === 'csv')
      ? [
          {
            id: 'open-panel',
            label: 'Open in panel',
            icon: <Maximize2 className="h-4 w-4" />,
            onClick: handleOpenInPanel,
          },
        ]
      : []),
  ]

  switch (type) {
    case 'markdown':
    case 'list':
      return (
        <ContextMenuWrapper actions={contextActions}>
          <EnhancedMarkdown
            style={{ backgroundColor: 'transparent', color: '#374151' }}
            className="prose prose-sm max-w-none"
          >
            {resolvedContent}
          </EnhancedMarkdown>
        </ContextMenuWrapper>
      )

    case 'html':
      return (
        <ContextMenuWrapper actions={contextActions}>
          <HtmlContentViewer content={resolvedContent} />
        </ContextMenuWrapper>
      )

    case 'csv':
      return (
        <ContextMenuWrapper actions={contextActions}>
          <EnhancedMarkdown
            style={{ backgroundColor: 'transparent', color: '#374151' }}
            className="prose prose-sm max-w-none"
          >
            {csvToMarkdownTable(resolvedContent)}
          </EnhancedMarkdown>
        </ContextMenuWrapper>
      )

    case 'url':
      const fileInfo = getFileTypeFromUrl(resolvedContent)
      const fileActions = [
        {
          id: 'copy-url',
          label: 'Copy URL',
          icon: <Copy className="h-4 w-4" />,
          onClick: handleCopy,
        },
        {
          id: 'open-file',
          label: 'Open in new tab',
          icon: <ExternalLink className="h-4 w-4" />,
          onClick: handleOpenFile,
        },
      ]

      return (
        <ContextMenuWrapper actions={fileActions}>
          <div className="rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4">
            <div className="flex items-center gap-3">
              <div className="text-2xl">{fileInfo.icon}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">{fileInfo.type}</div>
                <div className="truncate text-xs text-gray-600">{resolvedContent}</div>
              </div>
              <button
                onClick={handleOpenFile}
                className="flex items-center gap-2 rounded-lg border border-blue-200 px-3 py-1 text-sm text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
              >
                <ExternalLink size={14} />
                Open
              </button>
            </div>
          </div>
        </ContextMenuWrapper>
      )

    default:
      return (
        <ContextMenuWrapper actions={contextActions}>
          <div className="rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4">
            <pre className="whitespace-pre-wrap break-words font-mono text-sm text-gray-800">
              {resolvedContent}
            </pre>
          </div>
        </ContextMenuWrapper>
      )
  }
}
